import * as types from '../api/types';

/**
 * Pure helpers for the Job detail checklist section and the checklist print
 * block (tracker item 44, phase 4). Validation mirrors the backend's
 * JobChecklistBusinessLayer messages.
 */

/** "3/7" */
export function checklistProgressLabel(progress: Pick<types.JobChecklistProgress, 'completed' | 'total'>): string {
  return `${progress.completed}/${progress.total}`;
}

/** Shown on the section and in the "complete this job?" confirm. Empty when nothing required is open. */
export function requiredStepsWarning(requiredIncomplete: number): string {
  if (requiredIncomplete <= 0) return '';
  return `${requiredIncomplete} required checklist step${requiredIncomplete === 1 ? ' is' : 's are'} not ticked.`;
}

/** Text for a new or edited step: trimmed 1-500 characters. Null when valid. */
export function checklistStepTextError(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return 'Step text is required';
  if (trimmed.length > types.CHECKLIST_STEP_TEXT_MAX) return `Step text cannot exceed ${types.CHECKLIST_STEP_TEXT_MAX} characters`;
  return null;
}

export function checklistProgressOf(checklist: types.JobChecklist): types.JobChecklistProgress {
  return { total: checklist.total, completed: checklist.completed, requiredIncomplete: checklist.requiredIncomplete };
}

/** Recount every group and the totals (groups with no steps left are dropped). */
function recount(jobId: string, groups: types.JobChecklistGroup[]): types.JobChecklist {
  let total = 0;
  let completed = 0;
  let requiredIncomplete = 0;
  const kept = groups
    .filter((group) => group.steps.length > 0)
    .map((group) => {
      const done = group.steps.filter((step) => step.completed).length;
      total += group.steps.length;
      completed += done;
      requiredIncomplete += group.steps.filter((step) => step.required && !step.completed).length;
      return { ...group, total: group.steps.length, completed: done };
    });
  return { jobId, total, completed, requiredIncomplete, groups: kept };
}

/** The checklist with one step replaced by the server's copy (tick, untick, edit). */
export function replaceChecklistStep(checklist: types.JobChecklist, step: types.JobChecklistStep): types.JobChecklist {
  return recount(checklist.jobId, checklist.groups.map((group) => ({
    ...group,
    steps: group.steps.map((existing) => (existing.id === step.id ? step : existing)),
  })));
}

/** The checklist without one step (empty groups disappear). */
export function removeChecklistStep(checklist: types.JobChecklist, stepId: string): types.JobChecklist {
  return recount(checklist.jobId, checklist.groups.map((group) => ({
    ...group,
    steps: group.steps.filter((step) => step.id !== stepId),
  })));
}

/** Group names a new step can join; the default group always comes first. */
export function checklistGroupNameOptions(checklist: types.JobChecklist | null): string[] {
  const names = new Set<string>([types.JOB_CHECKLIST_DEFAULT_GROUP]);
  checklist?.groups.forEach((group) => names.add(group.groupName));
  return [...names];
}

export interface ChecklistTemplateOption {
  id: string;
  label: string;
  /** The job already has this template's group: applying it would change nothing */
  onJob: boolean;
}

/** Active templates for the "Apply template" picker, by name; those already on the job are marked. */
export function checklistTemplateOptions(
  templates: types.ChecklistTemplate[],
  checklist: types.JobChecklist | null
): ChecklistTemplateOption[] {
  const onJob = new Set((checklist?.groups ?? []).map((group) => group.sourceTemplateId).filter(Boolean));
  return templates
    .filter((template) => template.isActive)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((template) => ({
      id: template.id,
      onJob: onJob.has(template.id),
      label: `${template.name} (${template.stepCount} step${template.stepCount === 1 ? '' : 's'})${onJob.has(template.id) ? ' - already on this job' : ''}`,
    }));
}

/** Groups worth printing: those with at least one step. */
export function printableChecklistGroups(checklist: types.JobChecklist | null | undefined): types.JobChecklistGroup[] {
  return (checklist?.groups ?? []).filter((group) => group.steps.length > 0);
}
